import React, { useEffect, useState } from 'react';
import forOwn from 'lodash/forOwn';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import {
  Button,
  Sheet,
  Container,
  Row,
  Col,
  SearchInput,
  FilterIcon,
  BodyText,
  Combobox,
  Divider,
  RadioButton,
  Link,
  ArrowLeftIcon,
} from '@paypalcorp/pp-react';
import { v2neutral200 } from '@paypalcorp/pp-react-colors';
import styles from './searchBoxWithSheet.less';
import BadgeList from '../badgeList/badgeList';
import * as analytics from '../../lib/analytics';
import MediaQuery from 'react-responsive';
import { css } from 'glamor';

const dividerStyle = css({
  borderTop: '8px solid ' + v2neutral200 + ' !important',
});

const SearchBoxWithSheet = (
  {
    categories,
    fundraiserCategories,
    states,
    isMobile,
    i18n,
    searchSubmitUrl,
    query,
    supportedProduct,
    authState,
    contentCountry,
  },
  ...props
) => {
  const [keywords, setKeywords] = useState('');
  const [filters, setFilters] = useState([]);
  const [categoryValueOptions, setCategoryValueOptions] = useState([]);
  const [stateValueOptions, setStateValueOptions] = useState([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [removePill, setRemovePill] = useState(false);
  const [clearKeywords, setClearKeywords] = useState(false);
  const [selectedCauseType, setSelectedCauseType] = useState('all');
  const [selectedState, setSelectedState] = useState({
    label: '',
    value: '',
  });
  const [selectedCategory, setSelectedCategory] = useState({
    label: '',
    value: '',
  });

  const analyticsData = {
    e: 'cl',
    product: 'dw_giving',
    event_source: analytics.getEventSource(isMobile),
    account_type: analytics.getAccountType(authState),
    acct_cntry: contentCountry,
  };

  useEffect(() => {
    let options;
    const supportedCategories =
      query.type === 'charities' ? categories : fundraiserCategories;
    if (Array.isArray(supportedCategories)) {
      const categoryIDs = uniq(supportedCategories.map((ca) => ca.id));
      options = categoryIDs.map((id) => ({
        value: String(id),
        label:
          query.type === 'charities'
            ? i18n(`label.category-${id}`)
            : i18n(`label.fundraiser-category-${id}`),
      }));
      if (options.length > 0) {
        options.unshift({
          value: 'all',
          label: i18n('label.search-option-all-categories'),
        });
      }
    } else {
      options = [];
    }
    setCategoryValueOptions(options);
  }, [categories, fundraiserCategories]);

  useEffect(() => {
    const options = [];
    forOwn(states, (value, key) => {
      options.push({
        value: key,
        label: value,
      });
    });
    if (options.length > 0) {
      options.unshift({
        value: 'all',
        label: i18n('label.search-option-all-states'),
      });
    }
    setStateValueOptions(options);
  }, [states]);

  useEffect(() => {
    if (query) {
      if (query.keywords) {
        setKeywords(query.keywords);
      }

      if (query.type) {
        setSelectedCauseType(query.type);
      }
    }
  }, [query]);

  useEffect(() => {
    if (categoryValueOptions.length > 0) {
      setSelectedCategory(categoryValueOptions[0]);

      if (query && query.category) {
        let category;
        category = categoryValueOptions.find((obj) => {
          return obj.value === query.category;
        });
        setSelectedCategory(category || categoryValueOptions[0]);
      }
    }
  }, [categoryValueOptions]);

  useEffect(() => {
    if (stateValueOptions.length > 0) {
      setSelectedState(stateValueOptions[0]);

      if (query && query.state) {
        const state = stateValueOptions.find((obj) => {
          return obj.value === query.state;
        });
        setSelectedState(state || stateValueOptions[0]);
      }
    }
  }, [stateValueOptions]);

  useEffect(() => {
    const filtersList = [];

    if (isLoading && selectedCategory.value) {
      if (supportedProduct === 'all' && selectedCauseType !== 'all') {
        filtersList.push({
          type: 'causeType',
          value: selectedCauseType.value,
          primaryText: i18n(`label.${selectedCauseType}`),
        });
      }

      if (selectedState.value && selectedState.value !== 'all') {
        filtersList.push({
          type: 'state',
          value: selectedState.value,
          primaryText: selectedState.label,
        });
      }

      if (selectedCategory.value !== 'all') {
        filtersList.push({
          type: 'category',
          value: selectedCategory.value,
          primaryText: selectedCategory.label,
        });
      }

      setIsLoading(false);
      setFilters(filtersList);
    }
  }, [selectedState, selectedCategory, selectedCauseType]);

  function recordSearchEvent(keyword) {
    analytics.instance.recordEvent(analytics.events.DW_GIVING_SEARCH_PRESSED, {
      ...analyticsData,
      keyword,
    });
  }

  function recordFilterPressedEvent() {
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_BROWSE_FUNDRAISER_CHARITY_FILTER_PRESSED,
      analyticsData
    );
  }

  function recordFilterScreenShownEvent() {
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_SEARCH_FILTER_SCREEN_SHOWN,
      Object.assign({}, analyticsData, {
        e: 'im',
      })
    );
  }

  function recordClearFilterEvent() {
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_SEARCH_FILTER_CLEAR_PRESSED,
      analyticsData
    );
  }

  function recordFilterDismissEvent() {
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_SEARCH_FILTER_DISMISS_PRESSED,
      Object.assign({}, analyticsData, {
        dismiss_method: 'close',
      })
    );
  }

  const onFormSubmitted = () => {
    let url;
    if (!isEmpty(searchSubmitUrl)) {
      url = new URL(window.location.origin);
      url.pathname = searchSubmitUrl;
    } else {
      url = new URL(window.location.href);
    }
    const search = new URLSearchParams();
    search.append('keywords', keywords || '');
    search.append(
      'type',
      supportedProduct === 'all' ? selectedCauseType || 'all' : supportedProduct
    );
    if (
      selectedCategory.value &&
      !isEmpty(selectedCategory.value) &&
      selectedCategory.value !== 'all'
    ) {
      search.append('category', selectedCategory.value);
    }

    if (
      selectedState.value &&
      !isEmpty(selectedState.value) &&
      selectedState.value !== 'all'
    ) {
      search.append('state', selectedState.value);
    }

    search.append('page', '1');

    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    search.append('pageSize', urlParams.pageSize ? urlParams.pageSize : 24);

    search.append('sortBy', urlParams.sortBy ? urlParams.sortBy : 'bestmatch');

    url.search = search;
    window.location = url;
  };

  function recordFilterApplyEvent() {
    onFormSubmitted();
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_SEARCH_FILTER_APPLY_PRESSED,
      Object.assign({}, analyticsData, {
        filter_type: selectedCauseType,
        state: selectedState.value ? selectedState.value : '',
        category_code: selectedCategory.value ? selectedCategory.value : '',
      })
    );
  }

  const onKeywordChanged = (e) => setKeywords(e.target.value);
  const onTypeChanged = (e) => setSelectedCauseType(e.target.value);
  const closeSheet = () => {
    recordFilterDismissEvent();
    setIsSheetOpen(false);
  };
  const openSheet = () => {
    recordFilterPressedEvent();
    setIsSheetOpen(true);
  };

  const onStateChanged = (e) => {
    const state = stateValueOptions.find((obj) => {
      return obj.value === e.target.value;
    });
    setSelectedState(state);
  };

  const onCategoryChanged = (e) => {
    const category = categoryValueOptions.find((obj) => {
      return obj.value === e.target.value;
    });
    setSelectedCategory(category);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      recordSearchEvent(keywords);
      onFormSubmitted();
    }
  };

  const handleClearFilters = () => {
    recordClearFilterEvent();
    setSelectedState(stateValueOptions[0]);
    setSelectedCategory(categoryValueOptions[0]);
    setSelectedCauseType('all');
  };

  const handleClearKeywords = () => {
    if (keywords.length > 0) {
      setKeywords('');
      if (query.keywords) {
        setClearKeywords(true);
      }
    }
  };

  useEffect(() => {
    if (clearKeywords) {
      onFormSubmitted();
    }
  }, [clearKeywords]);

  const handleRemoveFilter = (pill) => {
    setRemovePill(true);

    if (pill.type === 'causeType') {
      setSelectedCauseType('all');
    }

    if (pill.type === 'category') {
      setSelectedCategory(categoryValueOptions[0]);
    }

    if (pill.type === 'state') {
      setSelectedState(stateValueOptions[0]);
    }
  };

  useEffect(() => {
    if (removePill) {
      onFormSubmitted();
    }
  }, [removePill]);

  const typeRadioButton = (
    <Row>
      <Col form='full'>
        {supportedProduct === 'all' && (
          <div>
            <BodyText>{i18n('label.by-type')}</BodyText>
            <RadioButton
              theme='v2'
              id='causeTypeRadioButton'
              onChange={onTypeChanged}
              value={selectedCauseType}
              className={styles.radioGroup}
              options={[
                {
                  label: i18n('label.search-option-all-categories'),
                  value: 'all',
                },
                {
                  label: i18n('label.charities'),
                  value: 'charities',
                },
                {
                  label: i18n('label.fundraisers'),
                  value: 'fundraisers',
                },
              ]}
            />
          </div>
        )}
      </Col>
    </Row>
  );

  const statesComboBox = (
    <Row>
      <Col form='full'>
        {stateValueOptions.length > 0 && (
          <div>
            <BodyText className={styles.stateText}>
              {i18n('label.by-state')}
            </BodyText>
            <Combobox
              id={'statesCombobox'}
              name='state'
              label={i18n('label.search-state')}
              mobileTitle={i18n('label.choose-state')}
              emptyMessage={i18n('label.combobox-no-results')}
              options={stateValueOptions}
              value={selectedState.label}
              onChange={onStateChanged}
            />
          </div>
        )}
      </Col>
    </Row>
  );

  const categoriesComboBox = (
    <Row>
      <Col form='full'>
        {categoryValueOptions.length > 0 && (
          <div>
            <BodyText className={styles.categoryText}>
              {i18n('label.by-category')}
            </BodyText>
            <Combobox
              id={'categoriesCombobox'}
              name='category'
              label={i18n('label.search-category')}
              mobileTitle={i18n('label.choose-category')}
              emptyMessage={i18n('label.combobox-no-results')}
              options={categoryValueOptions}
              value={selectedCategory.label}
              onChange={onCategoryChanged}
            />
          </div>
        )}
      </Col>
    </Row>
  );

  const footerFiltersSheet = (
    <Row>
      <Col form='full' className={styles.filterButtons}>
        <Button
          id={'applyFiltersButton'}
          className={styles.applyFiltersButton}
          theme='v2'
          onClick={recordFilterApplyEvent}
          size='lg'
        >
          {i18n('label.apply-filters')}
        </Button>
        <Button
          id={'clearFiltersButton'}
          className={styles.clearFiltersButton}
          theme='v2'
          size='lg'
          onClick={handleClearFilters}
          tertiary
        >
          {i18n('label.clear-filters')}
        </Button>
      </Col>
    </Row>
  );

  const renderfiltersSheet = () => (
    <>
      <MediaQuery minWidth={752}>
        <Sheet
          isOpen={isSheetOpen}
          onOpen={recordFilterScreenShownEvent}
          onClose={closeSheet}
          hideTitle={true}
          wrapperClassName={styles.sheetForm}
          containerClassName={styles.sheetFormContainer}
          skipFormFocus={true}
          showDrawer={true}
        >
          <Container>
            <Row>
              <Col form='full'>
                <h2
                  className={`ppvx_modal-content__title ${styles.filtersSheetTitle}`}
                >
                  {i18n('label.filter')}
                </h2>
              </Col>
            </Row>
            {typeRadioButton}
            {categoriesComboBox}
            {statesComboBox}
            {footerFiltersSheet}
          </Container>
        </Sheet>
      </MediaQuery>
      <MediaQuery maxWidth={751}>
        <Sheet
          isOpen={isSheetOpen}
          onOpen={recordFilterScreenShownEvent}
          onClose={closeSheet}
          title='Filter'
          footerAlign='center'
          wrapperClassName={styles.sheetForm}
          containerClassName={styles.sheetFormContainer}
          footerClassName={styles.sheetFooter}
          footerContents={footerFiltersSheet}
          skipFormFocus={true}
          noPadding={true}
        >
          <div>
            <div className={styles.comboBoxContainer}>{typeRadioButton}</div>
            <Divider className={`${dividerStyle}`} />
            <div className={styles.comboBoxContainer}>{categoriesComboBox}</div>
            <Divider className={`${dividerStyle}`} />
            <div className={styles.comboBoxContainer}>{statesComboBox}</div>
          </div>
        </Sheet>
      </MediaQuery>
    </>
  );

  let searchInputLabel = i18n('label.search-common-placeholder');
  if (query.type === 'charities') {
    searchInputLabel = i18n('label.search-placeholder');
  }

  if (query.type === 'fundraisers') {
    searchInputLabel = i18n('label.search-fundraiser-placeholder');
  }

  return (
    <div>
      <div className={styles.searchContainer}>
        <Link
          theme='v2'
          href={'/fundraiser/hub'}
          className={styles.fundraiserLink}
        >
          <Button tertiary iconComponent={ArrowLeftIcon}>
            <MediaQuery minWidth={752}>{i18n('label.back-nav')}</MediaQuery>
          </Button>
        </Link>

        <div className={styles.searchInputContainer}>
          <SearchInput
            id={'inputSearchBar'}
            label={searchInputLabel}
            name='keywords'
            value={keywords}
            onClear={handleClearKeywords}
            onChange={onKeywordChanged}
            onKeyUp={handleKeyPress}
          />
        </div>
        <div className={styles.filterButtonContainer}>
          <Button id={'filtersIconButton'} onClick={openSheet} icon tertiary>
            <FilterIcon size='md' aria-hidden='true' />
            <MediaQuery minWidth={752}>
              <span className={styles.filterSpan}> {i18n('label.filter')}</span>
            </MediaQuery>
          </Button>
        </div>
      </div>
      <div>
        <BadgeList {...props} items={filters} onRemove={handleRemoveFilter} />
      </div>
      {renderfiltersSheet()}
    </div>
  );
};

export default SearchBoxWithSheet;
